// Analyse Trade chart layout (29 Oct 2026) - DISPLAY ONLY. Pure helpers for the payoff chart: which underlying
// range to show (Focus on position / Full risk range), zoom and pan, the price grid the curves are valued on, the
// visible P&L range, and collision-free label lanes for the vertical markers.

const finite = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));

/** The default "Focus on position" window: every point of interest (spot, target, strikes, breakevens, rescue
 *  edges and triggers) plus padding, so the payoff's shape fills the chart. */
export function focusRange(points, { padPct = 0.12, minPadOfRef = 0.006 } = {}) {
  const xs = (points || []).filter(finite).map(Number).filter((x) => x > 0);
  if (!xs.length) return null;
  const lo = Math.min(...xs), hi = Math.max(...xs);
  const ref = (lo + hi) / 2;
  const pad = Math.max((hi - lo) * padPct, ref * minPadOfRef);
  return [Math.max(0, lo - pad), hi + pad];
}

/** "Full risk range": the focus window widened to show the tails - at least ±`widePct` of the reference price
 *  and 3× the focus span, so losses beyond the breakevens are clearly visible. */
export function fullRange(focus, ref, { widePct = 0.12, spanMult = 3 } = {}) {
  if (!focus) return null;
  const [lo, hi] = focus;
  const mid = finite(ref) ? Number(ref) : (lo + hi) / 2;
  const half = Math.max(((hi - lo) * spanMult) / 2, mid * widePct, (hi - lo) / 2);
  return [Math.max(0, Math.min(lo, mid - half)), Math.max(hi, mid + half)];
}

/** Zoom a [lo, hi] window by `factor` (<1 in, >1 out) around `center` (default its middle), kept inside `bounds`. */
export function zoomRange([lo, hi], factor, { center = null, bounds = null, minSpan = 1 } = {}) {
  const c = finite(center) && center >= lo && center <= hi ? Number(center) : (lo + hi) / 2;
  let span = Math.max(minSpan, (hi - lo) * factor);
  if (bounds) span = Math.min(span, bounds[1] - bounds[0]);
  const f = (c - lo) / (hi - lo || 1);
  return clampRange([c - span * f, c - span * f + span], bounds);
}

/** Pan a window by `frac` of its width (negative = left), kept inside `bounds`. */
export function panRange([lo, hi], frac, bounds = null) {
  const d = (hi - lo) * frac;
  return clampRange([lo + d, hi + d], bounds);
}

export function clampRange([lo, hi], bounds) {
  if (!bounds) return [Math.max(0, lo), hi];
  const span = hi - lo;
  if (lo < bounds[0]) return [bounds[0], Math.min(bounds[1], bounds[0] + span)];
  if (hi > bounds[1]) return [Math.max(bounds[0], bounds[1] - span), bounds[1]];
  return [lo, hi];
}

/** The prices the curves are valued at: an even grid over `range` plus every exact kink / marker, so the expiry
 *  curve's corners are exact at any zoom. */
export function priceGrid(range, extra = [], n = 241) {
  if (!range) return [];
  const [lo, hi] = range;
  const s = new Set();
  for (let i = 0; i < n; i += 1) s.add(Math.round((lo + ((hi - lo) * i) / (n - 1)) * 100) / 100);
  extra.filter(finite).map(Number).filter((x) => x >= lo && x <= hi).forEach((x) => s.add(Math.round(x * 100) / 100));
  return [...s].sort((a, b) => a - b);
}

/** [min, max] of the given keys over the points inside [lo, hi], padded 8 % (0 always included). */
export function visibleY(points, [lo, hi], keys) {
  let mn = 0, mx = 0;
  for (const p of points || []) {
    if (p.s < lo || p.s > hi) continue;
    for (const k of keys) {
      const v = p[k];
      if (finite(v)) { mn = Math.min(mn, v); mx = Math.max(mx, v); }
    }
  }
  const pad = Math.max((mx - mn) * 0.08, 1);
  return [mn - pad, mx + pad];
}

/** Collision-free label lanes. markers: [{x, label}]; returns the same markers with `lane` (0 = nearest the plot)
 *  for those inside [lo, hi] at `width` px, using ~`charPx` per character plus `gap`. Greedy: each label takes the
 *  lowest lane whose previous label ends before it starts. */
export function layoutLabels(markers, [lo, hi], width, { charPx = 5.6, gap = 6, maxLanes = 4 } = {}) {
  const span = hi - lo || 1;
  const inView = (markers || []).filter((m) => finite(m.x) && m.x >= lo && m.x <= hi)
    .map((m) => ({ ...m, px: ((m.x - lo) / span) * width, w: String(m.label || "").length * charPx }))
    .sort((a, b) => a.px - b.px);
  const ends = [];
  return inView.map((m) => {
    const start = Math.max(0, Math.min(width - m.w, m.px - m.w / 2));
    let lane = ends.findIndex((e) => e + gap <= start);
    if (lane < 0) lane = ends.length < maxLanes ? ends.length : ends.indexOf(Math.min(...ends));
    ends[lane] = start + m.w;
    return { ...m, lane, left: start };
  });
}

/** % move of `target` from `spot`, rounded to 2 dp. */
export const movePct = (spot, target) => (finite(spot) && finite(target) && Number(spot) ? Math.round(((target - spot) / spot) * 10000) / 100 : null);
